// Motivos de falha do período, prontos para quem não é técnico: título, o que
// significa, o que fazer e a prioridade. Alimenta as opções do "Exportar" e o
// PDF de falhas.
//
// A tradução é a MESMA da tela (libs/metaErrors) — o gestor que recebe o PDF e
// o operador que olha a lista precisam ler o mesmo nome para o mesmo problema.
// O backend agrupa por código da Meta (ou pelo texto, quando não há código);
// aqui juntamos as chaves que caem no mesmo título, porque códigos diferentes
// podem ser o mesmo problema para quem lê.

import { translateMetaError } from './metaErrors'
import { isMetaOfficialProvider } from './metaTemplates'

export const PRIORIDADES = {
  acao: { rotulo: 'Ação necessária', cor: '#d63a3a' },
  revisar: { rotulo: 'Revisar', cor: '#c97d06' },
  semAcao: { rotulo: 'Sem ação', cor: '#079669' },
}

// Quem tem de agir. "acao" é problema da CONTA ou do modelo — nada volta a sair
// até alguém mexer. "revisar" é problema de cadastro ou de uso, que se corrige
// no dia a dia. "semAcao" passa sozinho.
const PRIORIDADE_POR_CODIGO = {
  131042: 'acao', // pagamento da conta Meta
  132015: 'acao', // modelo pausado
  132016: 'acao', // modelo desativado
  132001: 'acao', // modelo não existe
  132000: 'acao', // parâmetros do modelo errados
  131048: 'acao', // qualidade do número
  131031: 'acao', // conta bloqueada
  130497: 'acao', // conta impedida no país
  131049: 'revisar',
  131026: 'revisar',
  131047: 'revisar',
  131050: 'revisar',
  130429: 'semAcao',
  131056: 'semAcao',
  130472: 'semAcao',
}

// Cores dos motivos no gráfico e nas bolinhas da tabela, na ordem de volume.
export const CORES_MOTIVOS = ['#d63a3a', '#8b5cf6', '#c97d06', '#0f766e', '#2563eb', '#db2777', '#64748b']

const normalizar = (t) => String(t || '').trim().replace(/\s+/g, ' ').toLowerCase()

// Falhas da conexão direta (não oficial): o texto já vem em português do
// backend. Os fixos mais comuns ganham explicação e ação próprias.
function motivoConexaoDireta(texto) {
  const t = String(texto || '').trim() || 'Motivo não informado'
  if (/mal formatado/i.test(t)) {
    return {
      titulo: 'Número de telefone mal formatado',
      explicacao: 'O número não segue o formato do WhatsApp (DDD + número com 9 dígitos).',
      acao: 'Corrija o número no cadastro do cliente.',
      prioridade: 'revisar',
    }
  }
  // Expiração por idade: o servidor grava "Expirada: mais de 24h na fila sem
  // envio". É a falha que mais confunde, porque a mensagem não deu erro nenhum
  // — ela só ficou esperando a vez (o caso típico é o teto diário do
  // aquecimento segurando a fila) até passar do prazo.
  // Adiada pelo teto diário por dias a fio: o servidor grava "Expirada: 3 dias
  // retida pelo limite diário do número". Vem antes das outras expirações
  // porque o motivo aqui é outro — não é fila lenta, é o número em
  // aquecimento sem vaga no limite do dia.
  if (/expir(ad|ou)/i.test(t) && /3\s*dias/i.test(t) && /limite\s+di[aá]rio/i.test(t)) {
    return {
      titulo: 'Expirou após 3 dias retida pelo limite diário',
      explicacao:
        'O número está em aquecimento e tem um limite de envios por dia. A mensagem ficou 3 dias esperando vaga nesse limite e foi descartada para não chegar atrasada ao cliente.',
      acao: 'Reenvie se ainda fizer sentido; o limite sobe sozinho com o aquecimento.',
      prioridade: 'revisar',
    }
  }
  if (/expir(ad|ou)/i.test(t) && /24\s*h/i.test(t)) {
    return {
      titulo: 'Expirou após 24h na fila',
      explicacao:
        'A mensagem esperou mais de 24h sem sair (por exemplo, retida pelo teto diário do aquecimento do número) e foi descartada para não chegar atrasada ao cliente.',
      acao: 'Reenvie se ainda fizer sentido para o cliente.',
      prioridade: 'revisar',
    }
  }
  if (/expir(ad|ou)/i.test(t)) {
    return {
      titulo: 'Mensagem expirou na fila',
      explicacao: 'A mensagem esperou tempo demais na fila e foi descartada para não chegar atrasada.',
      acao: 'Verifique se a conexão estava ativa e se o expediente permite o envio no horário.',
      prioridade: 'revisar',
    }
  }
  return {
    titulo: t,
    explicacao: 'O WhatsApp não aceitou a mensagem. O motivo exato está no próprio título.',
    acao: 'Se continuar acontecendo, fale com o suporte.',
    prioridade: 'revisar',
  }
}

export function traduzirMotivo(motivo, provider) {
  const erro = (motivo && motivo.erro) || {}
  if (!isMetaOfficialProvider(provider)) {
    return { ...motivoConexaoDireta(erro.text || motivo.chave), codigo: null }
  }
  const t = translateMetaError(erro) || translateMetaError({ text: motivo.chave })
  const codigo = (t && t.code) || erro.code || null
  return {
    titulo: (t && t.titulo) || 'Falha no envio pela Meta',
    explicacao: (t && t.explicacao) || 'A Meta não entregou esta mensagem.',
    acao: (t && t.acao) || 'Se persistir, fale com o suporte.',
    codigo,
    prioridade: PRIORIDADE_POR_CODIGO[codigo] || 'revisar',
  }
}

// [{ chave, total, erro, modelos }] do backend → motivos para o gestor, somando
// as chaves que viram o mesmo título. Ordenado por volume.
export function agruparMotivos(motivos, provider) {
  const porTitulo = new Map()
  ;(motivos || []).forEach((m) => {
    const t = traduzirMotivo(m, provider)
    const id = normalizar(t.titulo)
    const atual = porTitulo.get(id)
    if (atual) {
      atual.total += m.total || 0
      atual.chaves.push(m.chave)
      ;(m.modelos || []).forEach((x) => atual.modelos.add(x))
      if (!atual.codigo && t.codigo) atual.codigo = t.codigo
      return
    }
    porTitulo.set(id, {
      id,
      ...t,
      total: m.total || 0,
      chaves: [m.chave],
      modelos: new Set(m.modelos || []),
    })
  })
  return Array.from(porTitulo.values())
    .map((m) => ({ ...m, modelos: Array.from(m.modelos) }))
    .sort((a, b) => b.total - a.total || a.titulo.localeCompare(b.titulo, 'pt-BR'))
    .map((m, i) => ({ ...m, cor: CORES_MOTIVOS[Math.min(i, CORES_MOTIVOS.length - 1)] }))
}

// Motivo (já agrupado) de uma mensagem da lista detalhada.
export function motivoDaMensagem(mensagem, grupos) {
  const erro = mensagem && mensagem.erro
  const chave = (erro && (erro.code || erro.text)) || 'desconhecido'
  return (grupos || []).find((g) => g.chaves.includes(chave)) || null
}

const relatorioMotivosLib = { traduzirMotivo, agruparMotivos, motivoDaMensagem, PRIORIDADES, CORES_MOTIVOS }

export default relatorioMotivosLib
